import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Package,
  MapPin,
  Timer,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Calendar,
  Loader,
  Clock,
  Navigation,
  TruckIcon,
  BarChart3,
  AlertTriangle
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('driverToken');

  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // Get driver data from localStorage
        const driverData = JSON.parse(localStorage.getItem('driverData'));
        
        if (!driverData || (!driverData.email && !driverData._id)) {
          throw new Error('Driver data not found. Please log in again.');
        }
        
        const response = await axios.get('https://afric-fresh-backend.onrender.com/driver/stats', {
          headers: {
            Authorization: `Bearer ${token}`
          },
          params: {
            driverEmail: driverData.email,
            driverId: driverData._id
          }
        });
        
        setStats(response.data);
        setLoading(false);
      } catch (err) {
        console.log('Error fetching dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Custom tooltip for the chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-md shadow-md">
          <p className="font-medium text-gray-800">{label}</p>
          <p className="text-blue-600">
            <span className="font-medium">{payload[0].value}</span> deliveries
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-sm p-6 mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">Driver Dashboard</h1>
        <p className="text-gray-500 mb-6">
          Track your delivery performance and upcoming tasks
        </p>
        
        {/* Error display */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-md">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
            <button 
              onClick={() => window.location.reload()}
              className="mt-2 text-sm font-medium text-red-600 hover:text-red-800"
            >
              Try again
            </button>
          </div>
        )}
        
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
      

          {/* Pending Deliveries Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-purple-500 to-indigo-600 p-6 rounded-xl shadow-md text-white relative overflow-hidden"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-32">
                <Loader className="h-8 w-8 text-white animate-spin mb-2" />
                <p className="text-white text-opacity-80">Loading...</p>
              </div>
            ) : (
              <>
                <div className="absolute top-0 right-0 mt-4 mr-4 bg-white bg-opacity-20 rounded-full p-2">
                  <Timer className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-medium text-white text-opacity-90 mb-1">Pending Deliveries</h3>
                <div className="flex items-center">
                  <p className="font-bold text-3xl">{stats?.pendingCount || 0}</p>
                </div>
                <p className="mt-2 text-sm text-white text-opacity-80">
                  {stats?.pendingCount === 1 ? '1 delivery waiting' : `${stats?.pendingCount || 0} deliveries waiting`}
                </p>
              </>
            )}
          </motion.div>
          
          {/* Today's Deliveries Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-br from-blue-500 to-cyan-600 p-6 rounded-xl shadow-md text-white relative overflow-hidden"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-32">
                <Loader className="h-8 w-8 text-white animate-spin mb-2" />
                <p className="text-white text-opacity-80">Loading...</p>
              </div>
            ) : (
              <>
                <div className="absolute top-0 right-0 mt-4 mr-4 bg-white bg-opacity-20 rounded-full p-2">
                  <Package className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-medium text-white text-opacity-90 mb-1">Today's Deliveries</h3>
                <div className="flex items-center">
                  <p className="font-bold text-3xl">
                    {stats?.todayStats?.completed || 0}/{stats?.todayStats?.total || 0}
                  </p>
                </div>
                <div className="w-full bg-white bg-opacity-20 rounded-full h-2 mt-3">
                  <div 
                    className="bg-white h-2 rounded-full" 
                    style={{ 
                      width: `${stats?.todayStats?.total ? 
                        (stats.todayStats.completed / stats.todayStats.total) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                <p className="mt-2 text-sm text-white text-opacity-80">
                  {stats?.todayStats?.total ? 
                    Math.round((stats.todayStats.completed / stats.todayStats.total) * 100) : 0}% completed
                </p>
              </>
            )}
          </motion.div>
          
          {/* Total Deliveries Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-emerald-500 to-green-600 p-6 rounded-xl shadow-md text-white relative overflow-hidden"
          >
            {loading ? (
              <div className="flex flex-col items-center justify-center h-32">
                <Loader className="h-8 w-8 text-white animate-spin mb-2" />
                <p className="text-white text-opacity-80">Loading...</p>
              </div>
            ) : (
              <>
                <div className="absolute top-0 right-0 mt-4 mr-4 bg-white bg-opacity-20 rounded-full p-2">
                  <TruckIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-lg font-medium text-white text-opacity-90 mb-1">Total Deliveries</h3>
                <div className="flex items-center">
                  <p className="font-bold text-3xl">{stats?.totalDeliveries || 0}</p>
                </div>
                <p className="mt-2 text-sm text-white text-opacity-80">
                  Lifetime successful deliveries
                </p>
              </>
            )}
          </motion.div>
        </div>

        {/* Charts and Upcoming Deliveries */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Weekly Performance Chart */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-3 bg-white border border-gray-100 p-6 rounded-xl shadow-sm"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2 text-blue-500" />
                Weekly Performance
              </h3>
              {loading && <Loader className="h-4 w-4 text-gray-400 animate-spin" />}
            </div>
            
            {loading ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Loader className="h-10 w-10 text-blue-500 animate-spin mb-3" />
                <p className="text-gray-500">Loading weekly data...</p>
              </div>
            ) : !stats?.weeklyData?.length ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <AlertCircle className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500">No delivery data available for this week</p>
              </div>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar 
                      dataKey="deliveries" 
                      fill="#10b981" 
                      radius={[4, 4, 0, 0]}
                      barSize={36}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </motion.div>

          {/* Upcoming Deliveries */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 bg-white border border-gray-100 p-6 rounded-xl shadow-sm"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                <Navigation className="w-5 h-5 mr-2 text-indigo-500" />
                Upcoming Deliveries
              </h3>
              {loading && <Loader className="h-4 w-4 text-gray-400 animate-spin" />}
            </div>
            
            {loading ? (
              <div className="flex flex-col items-center justify-center h-64">
                <Loader className="h-10 w-10 text-indigo-500 animate-spin mb-3" />
                <p className="text-gray-500">Loading upcoming deliveries...</p>
              </div>
            ) : !stats?.upcomingDeliveries?.length ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Package className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-gray-500">No pending deliveries</p>
                <p className="text-sm text-gray-400">You're all caught up!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.upcomingDeliveries.map((delivery, index) => (
                  <motion.div
                    key={delivery.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className={`p-4 rounded-lg ${
                      delivery.status === 'Next'
                        ? 'bg-blue-50 border border-blue-100'
                        : 'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center">
                          {delivery.status === 'Next' ? (
                            <div className="w-2 h-2 rounded-full bg-blue-500 mr-2 animate-pulse"></div>
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-gray-400 mr-2"></div>
                          )}
                          <p className="font-medium text-gray-900">
                            Order #{delivery.orderNumber}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600 mt-1 flex items-center">
                          <MapPin className="h-3 w-3 text-gray-500 mr-1 flex-shrink-0" />
                          <span className="truncate">{delivery.address}</span>
                        </p>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Clock className="h-3 w-3 text-gray-500 mr-1" />
                        {delivery.time}
                      </div>
                    </div>
                    
                    <div className="mt-2 flex justify-between items-center">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${
                          delivery.status === 'Next'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-200 text-gray-800'
                        }`}
                      >
                        {delivery.status}
                      </span>
                      
                  
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default DashboardPage;